import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGraduationCap,
  FaBookOpen,
  FaCalendar,
  FaUser,
  FaBell,
  FaCog,
  FaSignOutAlt,
  FaHome,
  FaSearch,
  FaHeart,
  FaShoppingCart,
  FaUniversity,
  FaArrowRight,
  FaStar,
  FaUsers,
  FaGlobe,
  FaAward,
  FaChartLine,
  FaLightbulb,
  FaRocket,
  FaCheckCircle,
  FaClock,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import { useAuth } from '../../context/AuthContext';
import './UniversityHome.css';
import logo from '../../assets/logo.png';

const UniversityHome = () => {
  const navigate = useNavigate();
  const { token, profile, logout } = useAuth();
  const [userCriteria, setUserCriteria] = useState(null);
  const [courses, setCourses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!token || !profile) return;

      try {
        // Fetch user criteria
        const criteriaResponse = await axios.get(api.getUniversityCriteria(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (criteriaResponse.data.success && criteriaResponse.data.data.criteria) {
          setUserCriteria(criteriaResponse.data.data.criteria);
        } else {
          // If no criteria found, redirect to criteria form
          navigate('/university-criteria');
          return;
        }

        // Fetch recommended courses based on criteria
        const coursesResponse = await axios.get(api.getRecommendedCourses(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCourses(coursesResponse.data.data?.courses || []);

        // Fetch notifications
        const notificationsResponse = await axios.get(api.getUniversityNotifications(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        setNotifications(notificationsResponse.data.data?.notifications || []);

      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.response?.status === 404) {
          // No criteria found, redirect to criteria form
          navigate('/university-criteria');
          return;
        }
        toast.error('Failed to load university data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token, profile, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRecommendedPrograms = () => {
    if (!userCriteria) return [];

    const programMap = {
      'computer-science': {
        name: 'Computer Science',
        description: 'Learn programming, algorithms, and software development',
        duration: '4 Years',
        level: 'Bachelor\'s',
        image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=250&fit=crop',
        features: ['AI & Machine Learning', 'Software Engineering', 'Data Science', 'Cybersecurity'],
        careerPaths: ['Software Developer', 'Data Scientist', 'AI Engineer', 'Cybersecurity Analyst'],
      },
      'business-administration': {
        name: 'Business Administration',
        description: 'Develop leadership and management skills',
        duration: '4 Years',
        level: 'Bachelor\'s',
        image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
        features: ['Strategic Management', 'Marketing', 'Finance', 'Entrepreneurship'],
        careerPaths: ['Business Analyst', 'Marketing Manager', 'Financial Advisor', 'Entrepreneur'],
      },
      'engineering': {
        name: 'Engineering',
        description: 'Solve complex problems with innovative solutions',
        duration: '4 Years',
        level: 'Bachelor\'s',
        image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=250&fit=crop',
        features: ['Mechanical Engineering', 'Electrical Engineering', 'Civil Engineering', 'Chemical Engineering'],
        careerPaths: ['Mechanical Engineer', 'Electrical Engineer', 'Civil Engineer', 'Project Manager'],
      },
      'arts-humanities': {
        name: 'Arts & Humanities',
        description: 'Explore creativity and human expression',
        duration: '4 Years',
        level: 'Bachelor\'s',
        image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8a?w=400&h=250&fit=crop',
        features: ['Creative Writing', 'Visual Arts', 'Philosophy', 'History'],
        careerPaths: ['Content Creator', 'Art Director', 'Teacher', 'Researcher'],
      },
    };

    return [programMap[userCriteria.desiredProgram] || programMap['computer-science']];
  };

  const getUpcomingEvents = () => [
    {
      title: 'Virtual Open House',
      date: '2024-02-15',
      time: '2:00 PM',
      description: 'Join us for a virtual tour of our campus and programs',
      type: 'event',
      registration: true,
    },
    {
      title: 'Application Deadline',
      date: '2024-03-01',
      time: '11:59 PM',
      description: 'Final deadline for Fall 2024 applications',
      type: 'deadline',
      registration: false,
    },
    {
      title: 'Financial Aid Workshop',
      date: '2024-02-20',
      time: '6:00 PM',
      description: 'Learn about scholarships and financial aid options',
      type: 'workshop',
      registration: true,
    },
    {
      title: 'Career Fair',
      date: '2024-02-25',
      time: '10:00 AM',
      description: 'Connect with top employers and explore career opportunities',
      type: 'career',
      registration: true,
    },
  ];

  const getApplicationProgress = () => {
    if (!userCriteria) return 0;

    // Calculate progress based on criteria completion
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender', 'highestEducation', 'institutionName', 'graduationYear', 'major', 'desiredProgram', 'studyLevel', 'startDate', 'studyMode', 'goals'];
    const completedFields = requiredFields.filter(field => userCriteria[field]);
    return Math.round((completedFields.length / requiredFields.length) * 100);
  };

  const getNextSteps = () => [
    {
      title: 'Complete Application',
      description: 'Fill out your university application form',
      status: 'pending',
      priority: 'high',
    },
    {
      title: 'Submit Documents',
      description: 'Upload required academic documents',
      status: 'pending',
      priority: 'high',
    },
    {
      title: 'Financial Aid Application',
      description: 'Apply for scholarships and financial aid',
      status: 'pending',
      priority: 'medium',
    },
    {
      title: 'Schedule Interview',
      description: 'Book your admission interview',
      status: 'pending',
      priority: 'medium',
    },
  ];

  if (loading) {
    return (
      <div className="university-home-loading">
        <div className="loading-spinner"></div>
        <p>Loading your university dashboard...</p>
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <div className="university-home">
      {/* Header */}
      <header className="university-header">
        <div className="university-header-content">
          <div className="university-header-left">
            <img
              src={logo}
              alt="UlsaApp Logo"
              className="universitylogo-img "
            />
            <h1>UlsaApp University</h1>
          </div>
          <div className="university-header-right">
            <div className="user-info">
              <img
                src={getProfilePictureUrl(profile?.profilePicture, api.img, null, profile?.name || userCriteria?.firstName)}
                alt="Profile"
                className="user-avatar"
              />
              <div className="user-details">
                <h3>Welcome back, {userCriteria?.firstName || profile?.name}</h3>
                <p>{userCriteria?.desiredProgram ? userCriteria.desiredProgram.replace('-', ' ').toUpperCase() : 'Student'}</p>
              </div>
            </div>
            <div className="university-header-actions">
              <div className="university-notification-container">
                <button
                  className="university-notification-btn"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <FaBell />
                  {notifications.length > 0 && <span className="notification-badge">{notifications.length}</span>}
                </button>
                {showNotifications && (
                  <div className="notifications-dropdown">
                    <div className="notifications-header">
                      <h4>Notifications</h4>
                      <button onClick={() => setShowNotifications(false)}>×</button>
                    </div>
                    <div className="notifications-list">
                      {notifications.slice(0, 5).map((notification, index) => (
                        <div key={index} className="notification-dropdown-item">
                          <div className="notification-icon">
                            <FaBell />
                          </div>
                          <div className="notification-content">
                            <h5>{notification.title}</h5>
                            <p>{notification.message}</p>
                            <span>{new Date(notification.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                      {notifications.length === 0 && (
                        <p className="no-notifications">No new notifications</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button className="settings-btn" onClick={() => navigate('/university-settings')}>
                <FaCog />
              </button>
              <button className="logout-btn" onClick={handleLogout}>
                <FaSignOutAlt />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="university-nav">
        <div className="university-nav-container">
          <button
            className={`university-nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaHome /> Overview
          </button>
          <button
            className={`university-nav-tab ${activeTab === 'programs' ? 'active' : ''}`}
            onClick={() => setActiveTab('programs')}
          >
            <FaGraduationCap /> Programs
          </button>
          <button
            className={`university-nav-tab ${activeTab === 'application' ? 'active' : ''}`}
            onClick={() => setActiveTab('application')}
          >
            <FaCheckCircle /> Application
          </button>
          <button
            className={`university-nav-tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            <FaCalendar /> Events
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="university-main">
        <div className="university-main-container">
          {activeTab === 'overview' && (
            <>
              {/* Welcome Section */}
              <section className="welcome-section">
                <div className="welcome-content">
                  <div className="welcome-text">
                    <h2>Welcome to Your University Journey</h2>
                    <p>Based on your criteria, we've personalized your experience to help you achieve your educational goals.</p>
                    <div className="welcome-actions">
                      <button className="btn-primary" onClick={() => navigate('/university-application')}>
                        <FaRocket /> Start Application
                      </button>
                      <button className="btn-secondary" onClick={() => navigate('/university-courses')}>
                        <FaBookOpen /> Browse Courses
                      </button>
                    </div>
                  </div>
                  <div className="welcome-stats">
                    <div className="stat-item">
                      <FaUsers />
                      <div className="stat-content">
                        <span className="stat-number">50,000+</span>
                        <span className="stat-label">Students</span>
                      </div>
                    </div>
                    <div className="stat-item">
                      <FaGlobe />
                      <div className="stat-content">
                        <span className="stat-number">150+</span>
                        <span className="stat-label">Countries</span>
                      </div>
                    </div>
                    <div className="stat-item">
                      <FaAward />
                      <div className="stat-content">
                        <span className="stat-number">95%</span>
                        <span className="stat-label">Success Rate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Application Progress */}
              <section className="progress-section">
                <div className="section-header">
                  <h3>Application Progress</h3>
                  <p>Track your application status</p>
                </div>
                <div className="progress-card">
                  <div className="progress-info">
                    <h4>Overall Progress</h4>
                    <span className="progress-percentage">{getApplicationProgress()}%</span>
                  </div>
                  <div className="unihome-progress-bar">
                    <div
                      className="unihome-progress-fill"
                      style={{ width: `${getApplicationProgress()}%` }}
                    ></div>
                  </div>
                  <div className="progress-steps">
                    {getNextSteps().map((step, index) => (
                      <div key={index} className={`progress-step ${step.status}`}>
                        <div className="step-icon">
                          {step.status === 'completed' ? <FaCheckCircle /> : <FaClock />}
                        </div>
                        <div className="university-step-content">
                          <h5>{step.title}</h5>
                          <p>{step.description}</p>
                        </div>
                        <div className={`step-priority ${step.priority}`}>
                          {step.priority}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Quick Actions */}
              <section className="university-quick-actions">
                <div className="university-section-header">
                  <h3>Quick Actions</h3>
                  <p>Get started with your university journey</p>
                </div>
                <div className="university-actions-grid">
                  <div className="university-action-card" onClick={() => navigate('/university-courses')}>
                    <div className="university-action-icon">
                      <FaBookOpen />
                    </div>
                    <h4>Browse Courses</h4>
                    <p>Explore our comprehensive course catalog</p>
                    <FaArrowRight className="action-arrow" />
                  </div>
                  <div className="university-action-card" onClick={() => navigate('/university-application')}>
                    <div className="university-action-icon">
                      <FaUniversity />
                    </div>
                    <h4>Apply Now</h4>
                    <p>Start your application process</p>
                    <FaArrowRight className="action-arrow" />
                  </div>
                  <div className="university-action-card" onClick={() => navigate('/university-financial-aid')}>
                    <div className="university-action-icon">
                      <FaAward />
                    </div>
                    <h4>Financial Aid</h4>
                    <p>Explore funding options</p>
                    <FaArrowRight className="action-arrow" />
                  </div>
                  <div className="university-action-card" onClick={() => navigate('/university-events')}>
                    <div className="university-action-icon">
                      <FaCalendar />
                    </div>
                    <h4>Events</h4>
                    <p>Attend university events</p>
                    <FaArrowRight className="action-arrow" />
                  </div>
                </div>
              </section>
            </>
          )}

          {activeTab === 'programs' && (
            <section className="university-programs-section">
              <div className="university-section-header">
                <h3>Recommended for You</h3>
                <p>Programs that match your interests and goals</p>
              </div>
              <div className="university-programs-grid">
                {getRecommendedPrograms().map((program, index) => (
                  <div key={index} className="university-program-card">
                    <div className="university-program-image">
                      <img src={program.image} alt={program.name} />
                      <div className="university-program-overlay">
                        <button className="btn-primary">Learn More</button>
                      </div>
                    </div>
                    <div className="university-program-content">
                      <h4>{program.name}</h4>
                      <p>{program.description}</p>
                      <div className="university-program-meta">
                        <span><FaCalendar /> {program.duration}</span>
                        <span><FaGraduationCap /> {program.level}</span>
                      </div>
                      <div className="university-program-features">
                        <h5>Key Features:</h5>
                        <ul>
                          {program.features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="university-program-careers">
                        <h5>Career Paths:</h5>
                        <div className="university-career-tags">
                          {program.careerPaths.map((career, idx) => (
                            <span key={idx} className="university-career-tag">{career}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'application' && (
            <section className="university-application-section">
              <div className="university-section-header">
                <h3>Application Status</h3>
                <p>Track your application progress</p>
              </div>
              <div className="university-application-timeline">
                {getNextSteps().map((step, index) => (
                  <div key={index} className={`university-timeline-item ${step.status}`}>
                    <div className="university-timeline-icon">
                      {step.status === 'completed' ? <FaCheckCircle /> : <FaClock />}
                    </div>
                    <div className="university-timeline-content">
                      <h4>{step.title}</h4>
                      <p>{step.description}</p>
                      <div className="university-timeline-meta">
                        <span className={`university-priority-badge ${step.priority}`}>{step.priority}</span>
                        <span className="timeline-date">Due: {new Date(Date.now() + (index + 1) * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'events' && (
            <section className="university-events-section">
              <div className="university-section-header">
                <h3>Upcoming Events</h3>
                <p>Don't miss these important dates</p>
              </div>
              <div className="university-events-list">
                {getUpcomingEvents().map((event, index) => (
                  <div key={index} className={`university-event-item ${event.type}`}>
                    <div className="university-event-date">
                      <span className="university-event-day">{new Date(event.date).getDate()}</span>
                      <span className="university-event-month">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div className="university-event-details">
                      <div className="university-event-header">
                        <h4>{event.title}</h4>
                        <span className={`university-event-type ${event.type}`}>{event.type}</span>
                      </div>
                      <p>{event.description}</p>
                      <div className="university-event-meta">
                        <span className="university-event-time"><FaClock /> {event.time}</span>
                        <span className="university-event-location"><FaMapMarkerAlt /> Virtual Event</span>
                      </div>
                    </div>
                    {event.registration && (
                      <button className="university-event-btn">Register</button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="university-footer">
        <div className="university-footer-content">
          <div className="university-footer-section">
            <h4>UlsaApp University</h4>
            <p>Empowering minds, shaping futures, and building tomorrow's leaders.</p>
            <div className="university-social-links">
              <a href="#"><FaLinkedin /></a>
              <a href="#"><FaTwitter /></a>
              <a href="#"><FaFacebook /></a>
              <a href="#"><FaInstagram /></a>
            </div>
          </div>
          <div className="university-footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#programs">Programs</a></li>
              <li><a href="#admissions">Admissions</a></li>
              <li><a href="#financial-aid">Financial Aid</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          <div className="university-footer-section">
            <h4>Contact</h4>
            <div className="university-contact-info">
              <p><FaEnvelope /> info@UlsaApp.edu</p>
              <p><FaPhone /> (555) 123-4567</p>
              <p><FaMapMarkerAlt /> 123 University Ave, City, State 12345</p>
            </div>
          </div>
        </div>
        <div className="university-footer-bottom">
          <p>&copy; 2024 UlsaApp University. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default UniversityHome; 